import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, thumbUrl } from "@/lib/api";
import { useCachedGet, invalidateCache } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { estadoHorma, puedeEditarHormas } from "@/lib/hormas";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import LazyImage from "@/components/LazyImage";
import { Box, ChevronRight, Plus, Search } from "lucide-react";

const FILTROS = [
  { key: "todas", label: "Todas" },
  { key: "liberadas", label: "Liberadas" },
  { key: "desarrollo", label: "En desarrollo" },
  { key: "inactivas", label: "Inactivas" },
];

const norm = (s) => (s || "").toString().normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export default function Hormas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: hormas } = useCachedGet("/hormas");
  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState("todas");
  const [nueva, setNueva] = useState(null);
  const [busy, setBusy] = useState(false);
  const editar = puedeEditarHormas(user);

  // Búsqueda como la usan en Desarrollo: primero por nombre, luego código, luego proveedor.
  const lista = useMemo(() => {
    if (!hormas) return null;
    const t = norm(q.trim());
    const pasa = (h) =>
      filtro === "todas" ||
      (filtro === "liberadas" && h.activa && h.etapa === "liberada") ||
      (filtro === "desarrollo" && h.activa && h.etapa !== "liberada") ||
      (filtro === "inactivas" && !h.activa);
    const rango = (h) => {
      if (!t) return 0;
      if (norm(h.nombre).includes(t)) return 1;
      if (norm(h.codigo).includes(t)) return 2;
      if (norm(h.proveedor).includes(t)) return 3;
      return null;
    };
    return hormas
      .filter(pasa)
      .map((h) => [rango(h), h])
      .filter(([r]) => r !== null)
      .sort((a, b) => a[0] - b[0])
      .map(([, h]) => h);
  }, [hormas, q, filtro]);

  const crear = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hormas", { datos: nueva });
      invalidateCache("/hormas");
      toast.success("Horma dada de alta");
      navigate(`/hormas/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center"><Box size={18} /></div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Hormas</h1>
          </div>
          <p className="text-slate-500 mt-1">Catálogo de hormas. En los modelos solo se pueden elegir las revisadas o liberadas, y activas.</p>
        </div>
        {editar && (
          <Button onClick={() => setNueva({ codigo: "", nombre: "", proveedor: "", genero: "" })} data-testid="nueva-horma">
            <Plus size={16} className="mr-1" /> Nueva horma
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, código o proveedor" className="pl-9" data-testid="buscar-horma" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {FILTROS.map((f) => (
            <button key={f.key} onClick={() => setFiltro(f.key)} data-testid={`filtro-${f.key}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filtro === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!lista ? (
        <Skeleton className="h-72 rounded-xl" />
      ) : lista.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-dashed">
          {hormas.length === 0 ? "Aún no hay hormas en el catálogo." : "Ninguna horma coincide con la búsqueda."}
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
          {lista.map((h) => {
            const est = estadoHorma(h);
            return (
              <button key={h.id} onClick={() => navigate(`/hormas/${h.id}`)} data-testid={`horma-item-${h.codigo}`}
                className="w-full flex items-center gap-4 px-5 py-3 hover:bg-slate-50 transition-colors text-left">
                <div className="h-12 w-16 rounded-md bg-slate-50 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                  {h.foto_externa_id ? (
                    <LazyImage src={thumbUrl(h.foto_externa_id)} alt={h.nombre} containerClassName="w-full h-full" imgClassName="object-cover" />
                  ) : <Box size={18} className="text-slate-300" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-800 truncate">{h.nombre}</div>
                  <div className="text-xs text-slate-400 truncate">
                    <span className="font-mono text-slate-500">{h.codigo}</span>
                    {h.proveedor && ` · ${h.proveedor}`}
                    {h.genero && ` · ${h.genero}`}
                    {` · ${h.modelos_count} modelo${h.modelos_count === 1 ? "" : "s"}`}
                  </div>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${est.cls}`}>{est.label}</span>
                <ChevronRight size={18} className="text-slate-300 shrink-0" />
              </button>
            );
          })}
        </Card>
      )}

      <Dialog open={!!nueva} onOpenChange={(o) => !o && setNueva(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva horma</DialogTitle>
            <DialogDescription>Arranca en "Captura de medidas". Con las medidas y las dos fotos se libera.</DialogDescription>
          </DialogHeader>
          {nueva && (
            <div className="grid sm:grid-cols-2 gap-4">
              {[["codigo", "Código *"], ["nombre", "Nombre *"], ["proveedor", "Hormero / proveedor"], ["genero", "Género"]].map(([k, label]) => (
                <div key={k} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Input value={nueva[k]} onChange={(e) => setNueva({ ...nueva, [k]: e.target.value })} data-testid={`nueva-${k}`} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNueva(null)}>Cancelar</Button>
            <Button onClick={crear} disabled={busy || !nueva?.codigo?.trim() || !nueva?.nombre?.trim()} data-testid="crear-horma">Dar de alta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
